import React, { ReactNode } from 'react';
import Button, { ButtonProps } from '../../../common/Button/Button';
import { classes } from '../../../../lib/utils/cssUtils';
import styles from './ButtonWithActions.module.scss';
import Dropdown, { DropdownItem } from '../../../common/Dropdown/Dropdown';
import { useAttributeLabel } from '../../../../lib/hooks/labels';
import { ATTRIBUTES } from '../../../../lib/constants/attributes';
import { LABEL_KEYS } from '../../../../lib/constants/labelKeys';
import RefreshLoader from '../../../common/RefreshLoader/RefreshLoader';

export type ButtonWithActionsProps<T extends string> = {
  actions: {
    label: ReactNode;
    value: T;
  }[];
  onAction: (value: T) => void;
  variant?: ButtonProps['variant'];
  disabled?: boolean;
  classNames?: Partial<Record<'wrapper' | 'mainButton' | 'singleButton' | 'dropdownItem', string>>;
  isLoading?: boolean;
};

const ButtonWithActions = <T extends string>({
  actions,
  onAction,
  disabled,
  classNames,
  isLoading,
  variant = 'contained',
}: ButtonWithActionsProps<T>) => {
  const moreActionsLabel = useAttributeLabel(ATTRIBUTES.ARIA_LABEL, LABEL_KEYS.MORE_ACTIONS_LABEL);

  if (!actions || actions.length === 0) {
    return null;
  }

  if (actions.length === 1) {
    return (
      <Button
        className={classNames?.singleButton}
        variant={variant}
        onClick={() => onAction(actions[0].value)}
        disabled={disabled}
        isLoading={isLoading}
      >
        {actions[0].label}
      </Button>
    );
  }

  return (
    <div
      className={classes(styles.actionsButtonContainer, classNames?.wrapper, {
        [styles.secondary]: variant === 'outlined',
      })}
    >
      {isLoading && <RefreshLoader className={styles.loader} />}
      <Button
        variant={variant}
        onClick={() => onAction(actions[0].value)}
        className={classes(styles.buttonWithExtra, classNames?.mainButton)}
        disabled={disabled}
      >
        {actions[0].label}
      </Button>
      <Dropdown<T>
        SelectDisplayProps={moreActionsLabel}
        value=""
        variant={variant === 'outlined' ? 'primary' : 'secondary'}
        onChange={event => onAction(event.target.value as T)}
        classes={{
          select: styles.selectInput,
          icon: styles.icon,
          iconOpen: styles.iconOpened,
          disabled: styles.disabled,
        }}
        renderValue={() => null}
        className={classes(styles.dropdown, { [styles.disabled]: disabled })}
      >
        {actions.slice(1)?.map(({ label, value }) => (
          <DropdownItem key={value} value={value} className={classNames?.dropdownItem}>
            {label}
          </DropdownItem>
        ))}
      </Dropdown>
    </div>
  );
};

export default ButtonWithActions;
